import React, {useMemo, useState} from "react";
import {toast} from "react-toastify";
import {formattedDateTime} from "../../util/date";
import {confirmDelete} from "../common/confirmDelete";
import {GlobalStyles} from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import RemoveEventMutation from "./mutation/RemoveEvent";
import {getLocalizedGraphQLError} from "../../util/graphqlError";

export const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
        case "in_progress":
            return "danger";
        case "completed":
            return "success";
        default:
            return "secondary";
    }
};

export default function TrainingElement({
                                            event, handleRemoveEvent, onEdit, hideActions = false, selected = false,
                                            onToggleSelect,
                                        }) {
    const {t, language} = useTranslation();
    const colors = useThemeColors();
    const training = event?.trainingSession;
    const participants = training?.participants || [];
    const teams = training?.teams || [];
    const coaches = event?.responsibles || [];
    const managers = event?.managers || [];
    const status = event?.status || "SCHEDULED";
    const [participantsOpen, setParticipantsOpen] = useState(false);
    const toggleParticipants = () => setParticipantsOpen((v) => !v);
    const [removeEventMutation] = RemoveEventMutation();

    const scopeToken = useMemo(() => {
        const raw = String(event?.id ?? "x");
        return `kbola_trn_${raw.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
    }, [event?.id]);

    const scopeSel = `tr[data-kbola-trn="${scopeToken}"]`;

    const removeEvent = async (eventId) => {
        try {
            const res = await removeEventMutation({
                variables: {eventId},
            });

            if (res.data?.deleteEvent?.success) {
                handleRemoveEvent(eventId);

                toast.success(t("trainingElement.messages.removedSuccessfully"), {
                    autoClose: 3000,
                    toastId: "remove_training_success",
                    position: "top-center",
                    closeOnClick: true,
                });

                window.location.reload();
            } else {
                toast.error(
                    t("trainingElement.messages.failedToDelete"),
                    {
                        autoClose: 3000,
                        toastId: "remove_training_error",
                        position: "top-center",
                        closeOnClick: true,
                    }
                );
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t), {
                autoClose: 3000,
                toastId: "remove_training_error",
                position: "top-center",
                closeOnClick: true,
            });
        }
    };

    const requestDelete = (ev) => {
        confirmDelete({
            title: t("trainingElement.confirmDelete.title"),
            message: t("trainingElement.confirmDelete.message"),
            confirmLabel: t("trainingElement.confirmDelete.confirmLabel"),
            cancelLabel: t("trainingElement.confirmDelete.cancelLabel"),
            danger: true,
            onConfirm: async () => {
                await removeEvent(ev.id);
            },
        });
    };

    const stageLabel = event?.stage?.name || t("trainingElement.emptyValue");
    const title = event?.title || t("trainingElement.trainingSession");

    const whenText = formattedDateTime(event?.date, event?.timezone, language);
    const locationText = event?.field?.name || t("trainingElement.emptyValue");
    const coachesText =
        coaches.map((c) => c.fullName).join(", ") || t("trainingElement.emptyValue");

    const managersText =
        managers.map((c) => c.fullName).join(", ") || t("trainingElement.emptyValue");

    const showStatus = status !== "SCHEDULED";

    return (
        <tr data-kbola-trn={scopeToken} className="kbola-trn-row">
            <td className="align-middle text-center">
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={onToggleSelect}
                    disabled={event.status !== "SCHEDULED" || hideActions}
                />
            </td>
            <td className="align-middle">
                <span className="kbola-trn-pill kbola-trn-pillInfo">
                    {t("trainingElement.type")}
                </span>
            </td>

            <td className="align-middle">
                <span className="kbola-trn-pill kbola-trn-pillBrand">{stageLabel}</span>
            </td>

            <td className="align-middle" colSpan={1}>
                <div className="kbola-trn-titleWrap">
                    <div className="kbola-trn-title" title={title}>
                        {t("trainingElement.teamsCount", {count: teams.length})}
                    </div>
                    <div className="kbola-trn-sub">
                        {teams?.length ? (
                            teams
                                .map((st) => st?.team)
                                .filter(Boolean)
                                .map((team) => (
                                    <div key={team.id} className="kbola-trn-team">
                                        {team.name}
                                    </div>
                                ))
                        ) : (
                            <div>{t("trainingElement.noTeams")}</div>
                        )}
                    </div>
                </div>
            </td>

            <td className="align-middle" colSpan={2}>
                <div className="kbola-trn-titleWrap">
                    <button
                        type="button"
                        className="kbola-trn-collapsibleBtn"
                        onClick={toggleParticipants}
                        aria-expanded={participantsOpen}
                        aria-controls={`${scopeToken}_participants`}
                        title={
                            participantsOpen
                                ? t("trainingElement.hideParticipants")
                                : t("trainingElement.showParticipants")
                        }
                    >
                        <span className="kbola-trn-title">
                            {t("trainingElement.participantsCount", {
                                count: participants.length,
                            })}
                        </span>

                        <span className="kbola-trn-caret" aria-hidden="true">
                            <i
                                className={`fas fa-chevron-${participantsOpen ? "up" : "down"}`}
                            />
                        </span>
                    </button>

                    <div
                        id={`${scopeToken}_participants`}
                        className={`kbola-trn-collapsible ${participantsOpen ? "is-open" : ""}`}
                    >
                        {participants?.length ? (
                            participants.map((p) => (
                                <div key={p.id} className="kbola-trn-team">
                                    {p.fullName}
                                </div>
                            ))
                        ) : (
                            <div className="kbola-trn-empty">
                                {t("trainingElement.noParticipants")}
                            </div>
                        )}
                    </div>
                </div>
            </td>

            <td className="align-middle">
                {showStatus ? (
                    <span
                        className={`kbola-trn-pill kbola-trn-pillStatus kbola-trn-pill-${getStatusStyle(
                            status
                        )}`}
                    >
                        {t(`home.eventStatus.${status.toLowerCase()}`)}
                    </span>
                ) : (
                    <span className="kbola-trn-pill kbola-trn-pillMuted">
                        {t("trainingElement.scheduled")}
                    </span>
                )}
            </td>

            <td className="align-middle">
                <div className="kbola-trn-meta">
                    <span className="kbola-trn-pill kbola-trn-pillMuted">{whenText}</span>
                    <span className="kbola-trn-pill kbola-trn-pillMuted">
                        {t("trainingElement.locationLabel", {value: locationText})}
                    </span>
                    <span className="kbola-trn-pill kbola-trn-pillMuted">
                        {t("trainingElement.coachLabel", {value: coachesText})}
                    </span>
                    <span className="kbola-trn-pill kbola-trn-pillMuted">
                        {t("gameElement.managerLabel", {value: managersText})}
                    </span>
                </div>
            </td>

            {!hideActions && (
                <td className="project-actions text-right align-middle">
                    <div className="margin" key={event?.id}>
                        <div className="btn-group kbola-trn-actionsGroup">
                            <button type="button" className="btn btn-kbola btn-sm kbola-trn-actionsBtn">
                                {t("trainingElement.actions")}
                            </button>

                            <button
                                type="button"
                                className="btn btn-outline-kbola btn-sm dropdown-toggle dropdown-icon kbola-trn-actionsToggle"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                title={t("trainingElement.more")}
                            >
                                <span className="sr-only">
                                    {t("trainingElement.toggleDropdown")}
                                </span>
                            </button>

                            <div className="dropdown-menu dropdown-menu-right kbola-trn-actionsMenu" role="menu">
                                {status === "SCHEDULED" && (
                                    <button
                                        type="button"
                                        className="dropdown-item kbola-trn-actionsItem kbola-trn-dangerItem"
                                        onClick={() => requestDelete(event)}
                                    >
                                        <i className="fas fa-trash mr-2"/>
                                        {t("trainingElement.deleteSession")}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </td>
            )}

            <style>{`
          ${scopeSel} { font-family: 'Poppins', sans-serif; }

          ${scopeSel} i.fas,
          ${scopeSel} i.far,
          ${scopeSel} i.fal,
          ${scopeSel} i.fa,
          ${scopeSel} i[class^="fa-"],
          ${scopeSel} i[class*=" fa-"] {
            font-family: "Font Awesome 5 Free" !important;
            font-weight: 900 !important;
          }
          ${scopeSel} i.fab {
            font-family: "Font Awesome 5 Brands" !important;
            font-weight: 400 !important;
          }

          ${scopeSel}.kbola-trn-row:hover td {
            background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.08) !important;
          }

          ${scopeSel} .kbola-trn-pill {
            display: inline-flex !important;
            align-items: center !important;
            gap: 6px !important;
            padding: 6px 10px !important;
            border-radius: 999px !important;
            font-weight: 900 !important;
            font-size: 12px !important;
            border: 1px solid rgba(0,0,0,0.10) !important;
            line-height: 1 !important;
            white-space: nowrap !important;
            max-width: 100%;
          }
          ${scopeSel} .kbola-trn-team {
              font-size: 12px !important;
              font-weight: 700 !important;
              line-height: 1.25 !important;
              color: rgba(20,21,26,0.65) !important;
            }

          ${scopeSel} .kbola-trn-pillInfo {
            background: rgba(13,110,253,0.10) !important;
            color: rgba(13,110,253,0.95) !important;
            border-color: rgba(13,110,253,0.18) !important;
          }

          ${scopeSel} .kbola-trn-pillBrand {
            background: ${colors.secondary} !important;
            color: ${colors.textOnSecondary} !important;
            border-color: rgba(0,0,0,0.10) !important;
          }

          ${scopeSel} .kbola-trn-pillMuted {
            background: rgba(0,0,0,0.03) !important;
            color: rgba(20,21,26,0.72) !important;
            border-color: rgba(0,0,0,0.08) !important;
            font-weight: 800 !important;
          }

          ${scopeSel} .kbola-trn-pill-danger {
            background: rgba(220,53,69,0.10) !important;
            color: rgba(220,53,69,0.95) !important;
            border-color: rgba(220,53,69,0.18) !important;
          }
          ${scopeSel} .kbola-trn-pill-success {
            background: rgba(25,135,84,0.10) !important;
            color: rgba(25,135,84,0.95) !important;
            border-color: rgba(25,135,84,0.18) !important;
          }
          ${scopeSel} .kbola-trn-pillSuccess {
            background: ${GlobalStyles.colors.lightSuccess} !important;
            color: ${GlobalStyles.colors.success} !important;
            border-color: ${GlobalStyles.colors.success} !important;
          }
          ${scopeSel} .kbola-trn-pill-secondary {
            background: rgba(108,117,125,0.10) !important;
            color: rgba(108,117,125,0.95) !important;
            border-color: rgba(108,117,125,0.18) !important;
          }

          ${scopeSel} .kbola-trn-titleWrap {
            display: flex !important;
            flex-direction: column !important;
            gap: 4px !important;
            min-width: 0 !important;
            padding: 6px 0 !important;
          }
          ${scopeSel} .kbola-trn-title {
            font-size: 14px !important;
            font-weight: 900 !important;
            color: #14151a !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            max-width: 520px !important;
          }
          ${scopeSel} .kbola-trn-sub {
            font-size: 12px !important;
            font-weight: 800 !important;
            color: rgba(20,21,26,0.60) !important;
          }

          ${scopeSel} .kbola-trn-meta {
            display: flex !important;
            flex-direction: column !important;
            gap: 6px !important;
            align-items: flex-start !important;
            min-width: 0 !important;
          }

          ${scopeSel} .project-actions,
          ${scopeSel} .btn-group,
          ${scopeSel} .margin {
            overflow: visible !important;
          }

          ${scopeSel} .kbola-trn-actionsBtn,
          ${scopeSel} .kbola-trn-actionsToggle {
            border-radius: 12px !important;
            font-weight: 900 !important;
          }
          ${scopeSel} .kbola-trn-actionsBtn {
            border-top-right-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
          }
          ${scopeSel} .kbola-trn-actionsToggle {
            border-top-left-radius: 0 !important;
            border-bottom-left-radius: 0 !important;
            width: 42px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }

          ${scopeSel} .kbola-trn-actionsMenu {
            border-radius: 14px !important;
            border: 1px solid rgba(0,0,0,0.10) !important;
            box-shadow: 0 18px 44px rgba(0,0,0,0.12) !important;
            padding: 6px !important;
            min-width: 220px !important;
          }
          ${scopeSel} .kbola-trn-actionsItem {
            border-radius: 12px !important;
            font-weight: 850 !important;
            padding: 10px 10px !important;
            display: flex !important;
            align-items: center !important;
          }
          ${scopeSel} .kbola-trn-actionsItem:hover {
            background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.22) !important;
          }
          ${scopeSel} .kbola-trn-dangerItem {
            color: #dc3545 !important;
          }
          ${scopeSel} .kbola-trn-dangerItem:hover {
            background: rgba(220,53,69,0.08) !important;
          }

          ${scopeSel} button:focus,
          ${scopeSel} .btn:focus,
          ${scopeSel} .dropdown-item:focus {
            outline: none !important;
            box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18) !important;
          }

          ${scopeSel} .kbola-trn-collapsibleBtn {
            all: unset !important;
            cursor: pointer !important;
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 10px !important;
            width: 100% !important;
          }

          ${scopeSel} .kbola-trn-caret {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 26px !important;
            height: 26px !important;
            border-radius: 999px !important;
            background: rgba(0,0,0,0.04) !important;
            color: rgba(20,21,26,0.65) !important;
            flex: 0 0 auto !important;
          }

          ${scopeSel} .kbola-trn-collapsible {
            display: none !important;
            margin-top: 6px !important;
          }

          ${scopeSel} .kbola-trn-collapsible.is-open {
            display: block !important;
          }

          ${scopeSel} .kbola-trn-empty {
            font-size: 12px !important;
            font-weight: 800 !important;
            color: rgba(20,21,26,0.55) !important;
          }
          ${scopeSel} .kbola-trn-collapsible.is-open {
            max-height: 180px !important;
            overflow: auto !important;
            padding-right: 6px !important;
          }

          @media (max-width: 576px) {
            ${scopeSel} .kbola-trn-title { max-width: 240px !important; }
          }
        `}</style>
        </tr>
    );
}
